import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import useFloatingPanel from './useFloatingPanel';
import '../../assets/styles/ActionMenu.css';

const ActionMenu = ({ label, groups = [], className = '' }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const focusedRef = useRef(false);
  const { style, placement, ready } = useFloatingPanel(open, triggerRef, panelRef, { align: 'end' });

  const items = () => [...(panelRef.current?.querySelectorAll('.tp-menu__item:not(:disabled)') || [])];

  useEffect(() => {
    if (!open) {
      focusedRef.current = false;
      return;
    }
    if (ready && !focusedRef.current) {
      focusedRef.current = true;
      const first = items()[0];
      if (first) first.focus();
    }
  }, [open, ready]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointer = (event) => {
      const inside = rootRef.current?.contains(event.target) || panelRef.current?.contains(event.target);
      if (!inside) setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handlePanelKey = (event) => {
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const list = items();
    if (!list.length) return;
    const index = list.indexOf(document.activeElement);
    const last = list.length - 1;
    const next = {
      ArrowDown: index === last ? 0 : index + 1,
      ArrowUp: index <= 0 ? last : index - 1,
      Home: 0,
      End: last
    }[event.key];
    list[next].focus();
  };

  const handleSelect = (item) => {
    setOpen(false);
    triggerRef.current?.focus();
    item.onClick();
  };

  return (
    <div className={`tp-menu ${className}`.trim()} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`tp-menu__trigger ${open ? 'is-open' : ''}`.trim()}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((current) => !current)}
      >
        <MoreHorizontal size={18} strokeWidth={2} aria-hidden="true" />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className={`tp-menu__panel ${placement ? `is-${placement}` : ''}`.trim()}
          style={style}
          role="menu"
          aria-label={label}
          onKeyDown={handlePanelKey}
        >
          {groups.map((group, groupIndex) => (
            <div key={group.key || groupIndex} className="tp-menu__group" role="group" aria-label={group.title}>
              {group.title && <p className="tp-menu__title" aria-hidden="true">{group.title}</p>}
              {group.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className={`tp-menu__item ${item.danger ? 'is-danger' : ''}`.trim()}
                  disabled={item.disabled}
                  onClick={() => handleSelect(item)}
                >
                  {item.icon && <span className="tp-menu__icon" aria-hidden="true">{item.icon}</span>}
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionMenu;
export { ActionMenu };
